/**
 * Hook for polling text content from a local source.
 *
 * Two modes:
 * - Proxy mode (web client): fetches via the server proxy endpoint
 * - Bridge mode (mobile WebView): receives data via postMessage from React Native,
 *   which fetches directly from the source (no CORS restrictions, works offline)
 *
 * Supports both v2 JSON protocol (with formatting and long-polling) and
 * legacy HTML sources (with interval polling).
 *
 * Bridge mode is activated when window.POLYGRAF_NATIVE.useBridge is true.
 */

import type { DocumentState, TextSegment } from '@polygraf/shared'
import {
	applyV2Updates,
	buildLongPollUrl,
	buildViewerSourceUrl,
	getNativeToken,
	getNativeUseBridge,
	hasPollableSource,
	isWriterAwaitingPublisher,
	parseProxyErrorBody,
	pollErrorBackoffMs,
	proxyErrorTranslation,
	resolveApiBaseUrl,
	resolveFetchCredentials,
	type SourceProtocol,
	type V2ClientState,
	v2ToDocumentState,
	waitUntilVisible,
	withNativeBearer,
} from '@polygraf/shared'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'

const API_URL = resolveApiBaseUrl(import.meta.env.VITE_API_URL, 'http://localhost:4000')

/**
 * A failure whose message is already localized and safe to show. Anything else
 * reaching a catch block is a raw browser/network error — those must not be
 * rendered (the viewer used to print the proxy's English "URL not allowed"
 * straight into a Czech UI).
 */
class SourceFetchError extends Error {}

type Translate = ReturnType<typeof useTranslation>['t']

/**
 * Explain a non-OK source response. Prefers the proxy's structured reason —
 * "the address answered with a login page" is a lead, "HTTP 502" is not.
 */
async function describeFailure(response: Response, t: Translate): Promise<SourceFetchError> {
	const proxyError = parseProxyErrorBody(await response.json().catch(() => null))
	if (proxyError) {
		const { key, params } = proxyErrorTranslation(proxyError)
		return new SourceFetchError(t(key, params))
	}
	return new SourceFetchError(t('viewer.proxyError.generic', { status: response.status }))
}

interface ProxyTextResponse {
	paragraphs: Array<{
		id: string
		text: string
		segments?: TextSegment[]
		headingLevel?: 1 | 2 | 3
	}>
	contentHash: string
	fetchedAt: number
	/** Writer/transcriber display name, when the server knows it (MP-187). */
	writerName?: string
	v2?: {
		version: number
		title: string
		flatText: string
		inlineFormats: V2ClientState['inlineFormats']
		/** Version-sequence id (cloud writer only); versions restart under a new one. */
		epoch?: number
	}
	v2Update?: {
		title: string
		clear: boolean
		updates: unknown[]
		version: number
	}
}

interface UseLocalSourceTextOptions {
	url: string
	/** Direct URL to fetch from (bypasses proxy) */
	fetchUrl?: string
	/** Polling interval in milliseconds (default: 500) */
	pollInterval?: number
	enabled?: boolean
	/**
	 * Send no request while the page is hidden (background tab, locked phone).
	 * Set for the live-transcript HTTP fallback (card 422); local mode leaves it
	 * off so behaviour there is unchanged.
	 */
	pauseWhileHidden?: boolean
	/**
	 * Also wait `pollInterval` after a reply that carried new text, not only
	 * after a 204. Without it a busy writer drives one request per push.
	 */
	paceAfterUpdates?: boolean
}

/** The page's `Document`, if any. The hooks below shadow `document` with state. */
function pageDocument(): Document | undefined {
	return (globalThis as { document?: Document }).document
}

interface UseLocalSourceTextReturn {
	document: DocumentState | null
	/** Raw HTML from the source (bridge mode only) — render with dangerouslySetInnerHTML */
	rawHtml: string | null
	isLoading: boolean
	isPolling: boolean
	error: string | null
	/**
	 * True when the cloud writer endpoint answered 404 "no active writer" —
	 * the source is reachable but no publisher has pushed yet. UI should show
	 * "waiting for writer" rather than a hard error or a blank page.
	 */
	isWaitingForWriter: boolean
	hasNewContent: boolean
	refresh: () => void
}

/** Convert legacy proxy response (plain text paragraphs) to DocumentState */
function toLegacyDocumentState(data: ProxyTextResponse): DocumentState {
	return {
		id: 'local',
		title: '',
		paragraphs: data.paragraphs.map((p) => ({
			id: p.id,
			segments: [{ text: p.text }],
		})),
		version: data.fetchedAt,
		lastModified: data.fetchedAt,
		writerName: data.writerName, // MP-187
	}
}

/** Convert v2 bridge message (from React Native postMessage) to DocumentState */
function toV2BridgeDocumentState(data: {
	paragraphs: Array<{
		id: string
		text: string
		segments?: TextSegment[]
		headingLevel?: 1 | 2 | 3
	}>
	title?: string
	version?: number
	fetchedAt: number
}): DocumentState {
	return {
		id: 'local',
		title: data.title ?? '',
		paragraphs: data.paragraphs.map((p) => ({
			id: p.id,
			segments: p.segments ?? [{ text: p.text }],
			headingLevel: p.headingLevel,
		})),
		version: data.version ?? data.fetchedAt,
		lastModified: data.fetchedAt,
	}
}

/** Convert v2-enriched proxy response to DocumentState using v2ToDocumentState */
function toV2DocumentState(data: ProxyTextResponse): DocumentState {
	if (data.v2) {
		return {
			...v2ToDocumentState({
				flatText: data.v2.flatText,
				inlineFormats: data.v2.inlineFormats,
				version: data.v2.version,
				title: data.v2.title,
			}),
			writerName: data.writerName,
		} // MP-187
	}
	// Fallback: use paragraphs with segments if available
	return {
		id: 'local',
		title: '',
		paragraphs: data.paragraphs.map((p) => ({
			id: p.id,
			segments: p.segments ?? [{ text: p.text }],
			headingLevel: p.headingLevel,
		})),
		version: data.fetchedAt,
		lastModified: data.fetchedAt,
		writerName: data.writerName, // MP-187
	}
}

/**
 * Bridge mode: RN fetches data and sends it via postMessage.
 * No HTTP requests from the WebView — bypasses CORS entirely.
 */
function useBridgeMode({ enabled }: { enabled: boolean }): UseLocalSourceTextReturn {
	const { t } = useTranslation()
	// Same reason as the proxy-mode hook: `t` must not be a dependency of the
	// message listener, or a language switch would re-register it mid-stream.
	const tRef = useRef(t)
	tRef.current = t
	const [docState, setDocState] = useState<DocumentState | null>(null)
	const [rawHtml, setRawHtml] = useState<string | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [error, setError] = useState<string | null>(null)
	const [hasNewContent, setHasNewContent] = useState(false)
	const [isWaitingForWriter, setIsWaitingForWriter] = useState(false)
	const lastHashRef = useRef<string | null>(null)
	const hasContentRef = useRef(false)

	hasContentRef.current = docState !== null || rawHtml !== null

	useEffect(() => {
		if (!enabled) return

		const handleMessage = (event: MessageEvent) => {
			// Accept only events injected by React Native itself. react-native-webview
			// delivers `webView.postMessage(...)` as a synthetic
			// `new MessageEvent('message', {data})` — dispatched on `window` (iOS) or
			// `document` (Android) — whose `source` is null per the DOM spec. A real
			// cross-frame postMessage (iframe, opener, parent) always carries the
			// sender's WindowProxy, never null, so rejecting non-null foreign sources
			// keeps untrusted frames out of the rawHtml render path while letting the
			// native bridge through. (`source === window` self-posts stay accepted:
			// producing one requires script already running in this page.)
			if (event.source !== window && event.source !== null) return

			let data: unknown
			try {
				data = typeof event.data === 'string' ? JSON.parse(event.data) : event.data
			} catch {
				return // Ignore non-JSON messages
			}
			try {
				const msg = data as Record<string, unknown>

				if (msg.type === 'polygraf-transcript-html') {
					// Raw HTML from source — render directly
					const contentChanged =
						lastHashRef.current !== null && lastHashRef.current !== msg.contentHash
					lastHashRef.current = msg.contentHash as string

					if (contentChanged || !hasContentRef.current) {
						setRawHtml(msg.html as string)
						setHasNewContent(contentChanged)
					} else {
						setHasNewContent(false)
					}
					setError(null)
					setIsWaitingForWriter(false)
					setIsLoading(false)
				} else if (msg.type === 'polygraf-transcript-data') {
					// Structured paragraphs (with optional rich segments from v2)
					const contentChanged =
						lastHashRef.current !== null && lastHashRef.current !== msg.contentHash
					lastHashRef.current = msg.contentHash as string

					if (contentChanged || !hasContentRef.current) {
						const paragraphs = msg.paragraphs as Array<Record<string, unknown>>
						const hasSegments = paragraphs?.[0]?.segments
						setDocState(
							hasSegments
								? toV2BridgeDocumentState(msg as Parameters<typeof toV2BridgeDocumentState>[0])
								: toLegacyDocumentState(msg as unknown as ProxyTextResponse),
						)
						setHasNewContent(contentChanged)
					} else {
						setHasNewContent(false)
					}
					setError(null)
					setIsWaitingForWriter(false)
					setIsLoading(false)
				} else if (msg.type === 'polygraf-transcript-waiting') {
					// Native poller hit "writer not publishing yet" (404 on the
					// writer content endpoint). Show the waiting screen — but only
					// if we have no content yet, so a mid-session writer drop keeps
					// the last document visible rather than blanking (mirrors the
					// proxy long-poll's setIsWaitingForWriter(!hasDocument)).
					if (!hasContentRef.current) setIsWaitingForWriter(true)
					setError(null)
					setIsLoading(false)
				} else if (msg.type === 'polygraf-transcript-error') {
					setError(
						typeof msg.error === 'string'
							? msg.error
							: tRef.current('viewer.proxyError.connectionFailed'),
					)
					setIsWaitingForWriter(false)
					setIsLoading(false)
				}
			} catch (err) {
				console.error('[polygraf] Error processing bridge message:', err)
			}
		}

		window.addEventListener('message', handleMessage)
		// iOS WKWebView also fires on document
		globalThis.document.addEventListener('message', handleMessage as EventListener)

		return () => {
			window.removeEventListener('message', handleMessage)
			globalThis.document.removeEventListener('message', handleMessage as EventListener)
		}
	}, [enabled])

	const refresh = useCallback(() => {
		lastHashRef.current = null
		setIsWaitingForWriter(false)
		setIsLoading(true)
	}, [])

	return {
		document: docState,
		rawHtml,
		isLoading,
		isPolling: !isLoading && !error,
		error,
		// The native poller posts `polygraf-transcript-waiting` when the writer
		// hasn't announced yet; the WebView then shows the same waiting screen
		// as web (MU 2026-07-24).
		isWaitingForWriter,
		hasNewContent,
		refresh,
	}
}

/**
 * Proxy mode: fetches text from the server proxy or a direct URL.
 * Supports v2 JSON protocol with long-polling and legacy HTML with interval polling.
 */
function useProxyMode({
	url,
	fetchUrl,
	pollInterval = 500,
	enabled = true,
	pauseWhileHidden = false,
	paceAfterUpdates = false,
}: UseLocalSourceTextOptions): UseLocalSourceTextReturn {
	const { t } = useTranslation()
	// Held in a ref, not a dependency: `t` changes identity on a language switch,
	// and listing it in the poll callbacks would abort the long-poll, clear the
	// document and refetch — blanking a live transcript mid-lecture.
	const tRef = useRef(t)
	tRef.current = t
	const [document, setDocument] = useState<DocumentState | null>(null)
	const [isLoading, setIsLoading] = useState(true)
	const [isPolling, setIsPolling] = useState(false)
	const [error, setError] = useState<string | null>(null)
	const [isWaitingForWriter, setIsWaitingForWriter] = useState(false)
	const [hasNewContent, setHasNewContent] = useState(false)

	const lastHashRef = useRef<string | null>(null)
	const intervalRef = useRef<ReturnType<typeof setInterval> | null>(null)
	const abortRef = useRef<AbortController | null>(null)
	const hasDocumentRef = useRef(false)
	const protocolRef = useRef<SourceProtocol>('unknown')
	const v2StateRef = useRef<V2ClientState | null>(null)
	// Epoch of the version sequence v2StateRef.version belongs to, so the server
	// can tell a new sequence (Writer reconnected, versions restarted) from "no
	// change". Undefined for sources that never send one (LAN Writer).
	const v2EpochRef = useRef<number | undefined>(undefined)
	const longPollActiveRef = useRef(false)
	// MP-187: remember the last-known writer name so live long-poll v2 updates
	// (which may omit writerName) don't blank out the displayed name.
	const writerNameRef = useRef<string | undefined>(undefined)

	hasDocumentRef.current = document !== null
	writerNameRef.current = document?.writerName ?? writerNameRef.current

	/** Build the proxy URL, optionally with ?rev= for v2 long-polling */
	const buildProxyUrl = useCallback(
		(rev?: number) => {
			// `fetchUrl` set → LAN-direct mode (the writer is reachable from the
			// viewer's network). Otherwise we proxy `url` through the server. The
			// shared helper (ADR-0005) is the single source of truth for this
			// decision across all viewer call sites.
			const base =
				buildViewerSourceUrl({
					rawUrl: fetchUrl ?? url,
					format: 'TEXT',
					useProxy: !fetchUrl,
					apiUrl: API_URL,
				}) ?? ''
			if (rev === undefined) return base
			return buildLongPollUrl(base, rev, v2EpochRef.current)
		},
		[url, fetchUrl],
	)

	/** V2 long-poll loop — runs continuously until aborted */
	const startV2LongPoll = useCallback(
		async (parentController: AbortController) => {
			if (longPollActiveRef.current) return
			longPollActiveRef.current = true

			while (!parentController.signal.aborted && v2StateRef.current) {
				if (pauseWhileHidden) {
					await waitUntilVisible(pageDocument(), parentController.signal)
					if (parentController.signal.aborted) break
				}
				try {
					const targetUrl = buildProxyUrl(v2StateRef.current.version)
					// UAT B1: prod splits origins (polygraf.app → api.polygraf.app);
					// the default same-origin credentials mode silently dropped the
					// auth cookie, blanking the live view for authorized accounts.
					const headers = withNativeBearer(targetUrl, API_URL, getNativeToken())
					const res = await fetch(targetUrl, {
						signal: parentController.signal,
						headers,
						credentials: resolveFetchCredentials(targetUrl, API_URL),
						// Never let a redirect carry the bearer to another host; a LAN fetch
						// without it keeps following redirects as before.
						redirect: headers.has('Authorization') ? 'error' : 'follow',
					})

					if (parentController.signal.aborted) break

					// 204 = no new content. The server answers this immediately
					// (it does not hold the request open), so retrying with no delay
					// busy-loops at network speed — dozens of req/s per viewer, which
					// would hammer the API during a full lecture. Pace idle polls to
					// the same cadence as interval mode before retrying.
					if (res.status === 204) {
						await new Promise((r) => setTimeout(r, pollInterval))
						continue
					}

					// Writer disconnected mid-session: keep the last document on screen
					// and wait for re-announce instead of blanking into a hard error.
					if (isWriterAwaitingPublisher(res.status, targetUrl, API_URL)) {
						setIsWaitingForWriter(!hasDocumentRef.current)
						setError(null)
						await new Promise((r) => setTimeout(r, pollInterval))
						continue
					}

					if (!res.ok) {
						throw await describeFailure(res, tRef.current)
					}

					const data: ProxyTextResponse = await res.json()

					if (parentController.signal.aborted) break

					// Handle v2 update response
					if (data.v2Update && v2StateRef.current) {
						const updateResponse = data.v2Update as Parameters<typeof applyV2Updates>[1]
						v2StateRef.current = applyV2Updates(v2StateRef.current, updateResponse)
						// MP-187: incremental v2 updates carry no writerName — preserve
						// the last-known name so it doesn't vanish on the first update.
						const docState = {
							...v2ToDocumentState(v2StateRef.current),
							writerName: writerNameRef.current,
						}
						setDocument(docState)
						setHasNewContent(true)
						setError(null)
						setIsWaitingForWriter(false)
						if (paceAfterUpdates) await new Promise((r) => setTimeout(r, pollInterval))
						continue
					}

					// Handle full v2 response (server may re-send initial)
					if (data.v2) {
						v2StateRef.current = {
							flatText: data.v2.flatText,
							inlineFormats: data.v2.inlineFormats,
							version: data.v2.version,
							title: data.v2.title,
						}
						v2EpochRef.current = data.v2.epoch
						// MP-187: prefer a freshly-sent name, else keep the last-known one.
						if (data.writerName) writerNameRef.current = data.writerName
						setDocument({
							...toV2DocumentState(data),
							writerName: data.writerName ?? writerNameRef.current,
						})
						setHasNewContent(true)
						setError(null)
						setIsWaitingForWriter(false)
					}
					if (paceAfterUpdates) await new Promise((r) => setTimeout(r, pollInterval))
				} catch (err) {
					if (parentController.signal.aborted) break
					if (err instanceof DOMException && err.name === 'AbortError') break

					setError(
						err instanceof SourceFetchError
							? err.message
							: tRef.current('viewer.proxyError.connectionFailed'),
					)
					// Back off before retrying. A slowed poll (the live fallback) waits
					// at least its own cadence, so errors never raise its request rate.
					await new Promise((r) =>
						setTimeout(
							r,
							pollErrorBackoffMs({ intervalMs: pollInterval, pauseWhileHidden, paceAfterUpdates }),
						),
					)
				}
			}

			longPollActiveRef.current = false
		},
		[buildProxyUrl, pollInterval, pauseWhileHidden, paceAfterUpdates],
	)

	/** Fetch text (legacy interval polling or initial v2 fetch) */
	const fetchText = useCallback(
		async (isInitial: boolean) => {
			// Live cloud mode passes the source in `fetchUrl` with an empty `url`
			// (a cloud transcript has no ad-hoc URL param). Guarding on `!url`
			// alone bailed here, so the live HTTP-writer poll never fired — the
			// viewer flashed the WebSocket snapshot, then blanked on the switch to
			// this poll. `hasPollableSource` accepts either source (tested in shared).
			if (!hasPollableSource(url, fetchUrl)) return

			abortRef.current?.abort()
			const controller = new AbortController()
			abortRef.current = controller

			if (isInitial) {
				setIsLoading(true)
			}

			try {
				const targetUrl = buildProxyUrl()
				const headers = withNativeBearer(targetUrl, API_URL, getNativeToken())
				const res = await fetch(targetUrl, {
					signal: controller.signal,
					headers,
					credentials: resolveFetchCredentials(targetUrl, API_URL),
					// Never let a redirect carry the bearer to another host; a LAN fetch
					// without it keeps following redirects as before.
					redirect: headers.has('Authorization') ? 'error' : 'follow',
				})

				if (controller.signal.aborted) return

				// Pre-writer open (or writer pause with no stored doc): keep polling
				// and surface "waiting for writer" instead of a hard error.
				if (isWriterAwaitingPublisher(res.status, targetUrl, API_URL)) {
					setIsWaitingForWriter(true)
					setError(null)
					setIsPolling(true)
					return
				}

				if (!res.ok) {
					throw await describeFailure(res, tRef.current)
				}

				const data: ProxyTextResponse = await res.json()

				if (controller.signal.aborted) return

				// Detect v2 protocol on first response
				if (isInitial && data.v2) {
					protocolRef.current = 'v2-json'
					v2StateRef.current = {
						flatText: data.v2.flatText,
						inlineFormats: data.v2.inlineFormats,
						version: data.v2.version,
						title: data.v2.title,
					}
					v2EpochRef.current = data.v2.epoch

					setDocument(toV2DocumentState(data))
					setHasNewContent(true)
					setError(null)
					setIsWaitingForWriter(false)
					setIsPolling(true)
					setIsLoading(false)

					// Switch to long-poll loop instead of interval
					if (intervalRef.current) {
						clearInterval(intervalRef.current)
						intervalRef.current = null
					}
					startV2LongPoll(controller)
					return
				}

				if (isInitial && !data.v2) {
					protocolRef.current = 'html-legacy'
				}

				const contentChanged =
					lastHashRef.current !== null && lastHashRef.current !== data.contentHash
				lastHashRef.current = data.contentHash

				if (contentChanged || isInitial || !hasDocumentRef.current) {
					setDocument(toLegacyDocumentState(data))
					setHasNewContent(contentChanged)
				} else {
					setHasNewContent(false)
				}

				setError(null)
				setIsWaitingForWriter(false)
				setIsPolling(true)
			} catch (err) {
				if (err instanceof DOMException && err.name === 'AbortError') return
				if (controller.signal.aborted) return
				setError(
					err instanceof SourceFetchError
						? err.message
						: tRef.current('viewer.proxyError.connectionFailed'),
				)
				setIsWaitingForWriter(false)
			} finally {
				if (!controller.signal.aborted) {
					setIsLoading(false)
				}
			}
		},
		[url, fetchUrl, buildProxyUrl, startV2LongPoll],
	)

	useEffect(() => {
		// A source is either the ad-hoc `url` param or, in live cloud mode, the
		// explicit `fetchUrl` (the HTTP-writer endpoint). Bailing on `!url` alone
		// meant the live poll never started — see the fetchText guard above.
		if (!enabled || !hasPollableSource(url, fetchUrl)) {
			setIsLoading(false)
			abortRef.current?.abort()
			// Drop the text with the poll: the live viewer re-enables this hook for
			// each HTTP-fallback episode, and its first render must not take the
			// previous episode's copy for current (MU N3c, 7. 10. 2026).
			setDocument(null)
			return
		}

		// Reset state
		lastHashRef.current = null
		protocolRef.current = 'unknown'
		v2StateRef.current = null
		v2EpochRef.current = undefined
		longPollActiveRef.current = false
		setDocument(null)
		setError(null)
		setIsWaitingForWriter(false)
		setIsPolling(false)

		fetchText(true)

		// Start interval polling (will be cleared if v2 detected)
		intervalRef.current = setInterval(() => {
			if (pauseWhileHidden && pageDocument()?.hidden) return
			if (protocolRef.current !== 'v2-json') {
				fetchText(false)
			}
		}, pollInterval)

		return () => {
			if (intervalRef.current) {
				clearInterval(intervalRef.current)
				intervalRef.current = null
			}
			abortRef.current?.abort()
		}
	}, [url, fetchUrl, enabled, pollInterval, pauseWhileHidden, fetchText])

	const refresh = useCallback(() => {
		lastHashRef.current = null
		v2StateRef.current = null
		v2EpochRef.current = undefined
		protocolRef.current = 'unknown'
		longPollActiveRef.current = false
		fetchText(true)
	}, [fetchText])

	return {
		document,
		rawHtml: null,
		isLoading,
		isPolling,
		error,
		isWaitingForWriter,
		hasNewContent,
		refresh,
	}
}

export function useLocalSourceText(options: UseLocalSourceTextOptions): UseLocalSourceTextReturn {
	// Bridge mode requires both an explicit native config opt-in AND a real RN WebView
	// runtime (window.ReactNativeWebView present). The runtime check prevents bridge
	// mode from ever being active in a regular browser, where postMessage from any
	// page/iframe could otherwise inject untrusted HTML.
	const hasRNWebView =
		typeof window !== 'undefined' &&
		(window as { ReactNativeWebView?: unknown }).ReactNativeWebView !== undefined
	const useBridge = getNativeUseBridge() && hasRNWebView

	const bridgeResult = useBridgeMode({ enabled: useBridge && options.enabled !== false })
	const proxyResult = useProxyMode({
		...options,
		enabled: !useBridge && options.enabled !== false,
	})

	return useBridge ? bridgeResult : proxyResult
}
